import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { AppLayout } from "@/components/AppLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
  DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  FileText, 
  Plus, 
  Search, 
  MoreVertical, 
  Edit, 
  Trash2, 
  Download,
  History,
  Clock,
  CheckCircle2
} from "lucide-react";
import { toast } from "sonner";
import { NewContractMenu } from "@/components/NewContractMenu";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Contracts({ user, setUser }) {
  const navigate = useNavigate();
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteDialog, setDeleteDialog] = useState({ open: false, contract: null });

  useEffect(() => {
    fetchContracts();
  }, []);

  const fetchContracts = async () => {
    try {
      const response = await fetch(`${API_URL}/api/contracts`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setContracts(data);
      }
    } catch (error) {
      console.error('Error fetching contracts:', error);
      toast.error("Erreur lors du chargement des contrats");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteDialog.contract) return;

    try {
      const response = await fetch(`${API_URL}/api/contracts/${deleteDialog.contract.contract_id}`, {
        method: 'DELETE',
        credentials: 'include'
      });

      if (response.ok) {
        toast.success("Contrat supprimé");
        setContracts(contracts.filter(c => c.contract_id !== deleteDialog.contract.contract_id));
      } else {
        toast.error("Erreur lors de la suppression");
      }
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    }

    setDeleteDialog({ open: false, contract: null });
  };

  const handleDownload = async (contract) => {
    try {
      const response = await fetch(`${API_URL}/api/contracts/${contract.contract_id}/generate?format=docx`, {
        method: 'POST',
        credentials: 'include'
      });

      if (response.ok) {
        const data = await response.json();
        // Open download in new tab
        window.open(`${API_URL}${data.file_url}`, '_blank');
        toast.success("Document généré avec succès");
      } else {
        toast.error("Erreur lors de la génération");
      }
    } catch (error) {
      toast.error("Erreur lors de la génération");
    }
  };

  const handleStatusChange = async (contract, newStatus) => {
    if (contract.status === newStatus) return;
    try {
      const response = await fetch(`${API_URL}/api/contracts/${contract.contract_id}/status`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ status: newStatus })
      });
      if (response.ok) {
        const updated = await response.json();
        setContracts(contracts.map(c => c.contract_id === contract.contract_id ? { ...c, status: updated.status, updated_at: updated.updated_at } : c));
        toast.success("Statut mis à jour");
      } else {
        toast.error("Erreur lors du changement de statut");
      }
    } catch (error) {
      toast.error("Erreur lors du changement de statut");
    }
  };

  const filteredContracts = contracts.filter(c => 
    c.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    const variants = {
      draft: { label: "Brouillon", className: "bg-yellow-100 text-yellow-800" },
      active: { label: "Actif", className: "bg-green-100 text-green-800" },
      completed: { label: "Finalisé", className: "bg-blue-100 text-blue-800" }
    };
    const variant = variants[status] || variants.draft;
    return <Badge className={variant.className}>{variant.label}</Badge>;
  };

  return (
    <AppLayout user={user} setUser={setUser}>
      <div className="p-8" data-testid="contracts-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
          <div>
            <h1 className="font-heading text-3xl font-semibold tracking-tight mb-2">
              Mes contrats
            </h1>
            <p className="text-muted-foreground">
              Gérez et suivez vos contrats générés
            </p>
          </div>
          <NewContractMenu />
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            placeholder="Rechercher un contrat..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 max-w-md"
            data-testid="search-contracts-input"
          />
        </div>

        {/* Contracts Table */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="spinner w-8 h-8"></div>
          </div>
        ) : filteredContracts.length > 0 ? (
          <Card>
            <Table data-testid="contracts-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Nom</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Version</TableHead>
                  <TableHead>Dernière modification</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredContracts.map((contract) => (
                  <TableRow key={contract.contract_id} data-testid={`contract-row-${contract.contract_id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-muted rounded-lg flex items-center justify-center">
                          <FileText className="w-5 h-5 text-muted-foreground" />
                        </div>
                        <div>
                          <div className="font-medium">{contract.name}</div>
                          <div className="text-xs text-muted-foreground">
                            ID: {contract.contract_id}
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      {getStatusBadge(contract.status)}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline">v{contract.version}</Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1 text-sm text-muted-foreground">
                        <Clock className="w-3 h-3" />
                        {formatDate(contract.updated_at)}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" data-testid={`contract-menu-${contract.contract_id}`}>
                            <MoreVertical className="w-4 h-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => navigate(contract.source && contract.source !== 'template' ? `/contracts/content/${contract.contract_id}` : `/contracts/${contract.contract_id}`)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Modifier
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleDownload(contract)}>
                            <Download className="w-4 h-4 mr-2" />
                            Télécharger
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => navigate(`/contracts/${contract.contract_id}/versions`)}>
                            <History className="w-4 h-4 mr-2" />
                            Historique
                          </DropdownMenuItem>
                          <DropdownMenuSub>
                            <DropdownMenuSubTrigger data-testid={`contract-status-trigger-${contract.contract_id}`}>
                              <CheckCircle2 className="w-4 h-4 mr-2" />
                              Changer le statut
                            </DropdownMenuSubTrigger>
                            <DropdownMenuSubContent>
                              {[
                                { value: "draft", label: "Brouillon" },
                                { value: "active", label: "Actif" },
                                { value: "completed", label: "Finalisé" },
                              ].map((s) => (
                                <DropdownMenuItem
                                  key={s.value}
                                  disabled={contract.status === s.value}
                                  onClick={() => handleStatusChange(contract, s.value)}
                                  data-testid={`contract-status-${s.value}-${contract.contract_id}`}
                                >
                                  {contract.status === s.value && <CheckCircle2 className="w-4 h-4 mr-2 text-green-600" />}
                                  <span className={contract.status === s.value ? "font-medium" : "ml-6"}>{s.label}</span>
                                </DropdownMenuItem>
                              ))}
                            </DropdownMenuSubContent>
                          </DropdownMenuSub>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            onClick={() => setDeleteDialog({ open: true, contract })}
                            className="text-destructive"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Supprimer
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        ) : (
          <div className="empty-state" data-testid="empty-contracts">
            <div className="empty-state-icon">
              <FileText className="w-full h-full" />
            </div>
            <h3 className="font-semibold mb-2">Aucun contrat</h3>
            <p className="text-muted-foreground mb-4">
              {searchQuery 
                ? "Aucun contrat ne correspond à votre recherche" 
                : "Créez votre premier contrat à partir d'un modèle"}
            </p>
            {!searchQuery && (
              <Button onClick={() => navigate('/templates')}>
                <Plus className="w-4 h-4 mr-2" />
                Créer un contrat
              </Button>
            )}
          </div>
        )}

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={deleteDialog.open} onOpenChange={(open) => setDeleteDialog({ ...deleteDialog, open })}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Supprimer ce contrat ?</AlertDialogTitle>
              <AlertDialogDescription>
                Cette action est irréversible. Le contrat "{deleteDialog.contract?.name}" et tout son historique seront définitivement supprimés.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground">
                Supprimer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AppLayout>
  );
}
